import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { HiOutlineDocumentText, HiOutlineIdentification } from "react-icons/hi2";
import { PiShieldCheck, PiUsersThree } from "react-icons/pi";
import { FaArrowLeft } from "react-icons/fa";
import heroImage from "../../assets/img/dashboards/auth-hero.png";
import watermarkLogo from "../../assets/img/logo.webp";
import InputField from "../../components/fields/InputField";
import TextField from "../../components/fields/TextField";
import { useAuth } from "../../context/AuthContext";

const HERO_IMAGE = heroImage;
const API_BASE = (process.env.REACT_APP_API_BASE_URL || "http://localhost:5000").replace(/\/+$/, "");

const genderOptions = [
  { value: "", label: "Select Gender" },
  { value: "Male", label: "Male" },
  { value: "Female", label: "Female" },
  { value: "Other", label: "Other" },
];

const passwordStrength = (password = "") => {
  const rules = [/.{8,}/, /[A-Z]/, /[a-z]/, /\d/, /[^A-Za-z0-9]/];
  const score = rules.reduce((acc, rule) => acc + (rule.test(password) ? 1 : 0), 0);
  const labels = ["Too weak", "Weak", "Fair", "Good", "Strong", "Excellent"];
  return { score, label: labels[score], percent: (score / rules.length) * 100 };
};

export default function SignUp() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [form, setForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    fullName: "",
    dob: "",
    gender: "",
    mobile: "",
    address: "",
    country: "India",
    city: "",
    state: "",
    pinCode: "",
    aadharNumber: "",
    panNumber: "",
    aadharPhoto: null,
    panPhoto: null,
    education: "",
    profession: "",
    nomineeName: "",
    nomineeRelation: "",
    referralCode: "",
    sponsorName: "",
    terms: false,
    ageCheck: false,
  });

  const [error, setError] = useState({});
  const [loading, setLoading] = useState(false);

  // ============ EMAIL OTP VERIFICATION STATE ============
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  // ======================================================

  const passwordMeta = useMemo(() => passwordStrength(form.password), [form.password]);

  const handleChange = (e) => {
    const { id, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [id]: checked }));
      return;
    }

    if (type === "file") {
      setForm((prev) => ({ ...prev, [id]: files[0] }));
      return;
    }

    if (id === "fullName") {
      if (/\d/.test(value)) {
        setError((prev) => ({ ...prev, fullName: "Numbers are not allowed in full name" }));
        return;
      }
      if (value && value.length < 3) {
        setError((prev) => ({ ...prev, fullName: "Full name must be at least 3 characters" }));
      } else {
        setError((prev) => ({ ...prev, fullName: "" }));
      }
      const formattedValue = value.replace(/\s+/g, " ").replace(/^(.)|\s+(.)/g, (c) => c.toUpperCase());
      setForm((prev) => ({ ...prev, fullName: formattedValue }));
      return;
    }

    if (id === "mobile") {
      const numericValue = value.replace(/[^0-9]/g, "").slice(0, 10);
      setForm((prev) => ({ ...prev, mobile: numericValue }));
      setError((prev) => ({ ...prev, mobile: numericValue.length === 10 ? "" : prev.mobile }));
      return;
    }

    if (id === "email") {
      setOtp("");
      setOtpSent(false);
      setOtpVerified(false);
      setOtpTimer(0);
      setError((prev) => ({ ...prev, otp: "", email: "" }));
    }

    if (id === "dob" && value) {
      const dob = new Date(value);
      const today = new Date();
      if (isNaN(dob.getTime())) {
        setError((prev) => ({ ...prev, dob: "Invalid date" }));
      } else if (dob > today) {
        setError((prev) => ({ ...prev, dob: "Date cannot be in the future" }));
      } else {
        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
        if (age < 18) {
          setError((prev) => ({ ...prev, dob: "You must be at least 18 years old" }));
        } else {
          setError((prev) => ({ ...prev, dob: "" }));
        }
      }
    }

    setForm((prev) => ({ ...prev, [id]: value }));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const referralParam = params.get("ref") || params.get("referralId");
    if (referralParam) {
      const normalizedReferralCode = referralParam.trim().toUpperCase();
      setForm((prev) => ({ ...prev, referralCode: normalizedReferralCode }));
      fetchSponsorName(normalizedReferralCode);
    }
  }, [location.search]);

  useEffect(() => {
    if (!otpTimer) return;
    const timerId = setInterval(() => setOtpTimer((prev) => (prev > 0 ? prev - 1 : 0)), 1000);
    return () => clearInterval(timerId);
  }, [otpTimer]);

  const fetchSponsorName = async (referralCode) => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/validate-referral`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ referralCode })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        let errorMessage = data.message || "Invalid Referral Code";
        if (data.reason === "NO_PACKAGE") {
          errorMessage = "Referral not allowed. The referrer has not purchased any package.";
        } else if (data.reason === "PACKAGE_EXPIRED") {
          errorMessage = "Referral expired. The referrer's package validity has ended.";
        }
        setForm((prev) => ({ ...prev, sponsorName: "" }));
        setError((prev) => ({ ...prev, referralCode: errorMessage }));
        toast.error(errorMessage);
      } else {
        setForm((prev) => ({ ...prev, sponsorName: data.referrerName }));
        setError((prev) => ({ ...prev, referralCode: "" }));
      }
    } catch (err) {
      setForm((prev) => ({ ...prev, sponsorName: "" }));
      setError((prev) => ({ ...prev, referralCode: "Invalid Referral Code" }));
    }
  };

  const handlePincodeChange = async (e) => {
    const pinCode = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    setForm((prev) => ({ ...prev, pinCode }));

    if (pinCode.length === 6) {
      try {
        const res = await fetch(`https://api.postalpincode.in/pincode/${pinCode}`);
        const data = await res.json();
        if (data[0].Status === "Success") {
          const state = data[0].PostOffice[0].State;
          const city = data[0].PostOffice[0].District;
          setForm((prev) => ({ ...prev, state, city }));
          setError((prev) => ({ ...prev, pinCode: "" }));
          toast.success("State & City autofilled!");
        } else {
          setForm((prev) => ({ ...prev, state: "", city: "" }));
          setError((prev) => ({ ...prev, pinCode: "Invalid Pincode" }));
        }
      } catch (err) {
        setForm((prev) => ({ ...prev, state: "", city: "" }));
        setError((prev) => ({ ...prev, pinCode: "Failed to fetch location" }));
      }
    } else {
      setForm((prev) => ({ ...prev, state: "", city: "" }));
    }
  };

  const handleReferralChange = async (e) => {
    const referralCode = e.target.value.toUpperCase();
    setForm((prev) => ({ ...prev, referralCode }));

    if (referralCode.trim() !== "") {
      try {
        const res = await fetch(`${API_BASE}/api/auth/validate-referral`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ referralCode })
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          let errorMessage = data.message || "Invalid Referral Code";
          if (data.reason === "NO_PACKAGE") {
            errorMessage = "Referral not allowed. The referrer has not purchased any package.";
          } else if (data.reason === "PACKAGE_EXPIRED") {
            errorMessage = "Referral expired. The referrer's package validity has ended.";
          } else if (data.reason === "REFERRAL_DISABLED") {
            errorMessage = "Referral not enabled for this user.";
          }
          setForm((prev) => ({ ...prev, sponsorName: "" }));
          setError((prev) => ({ ...prev, referralCode: errorMessage }));
          toast.error(errorMessage);
        } else {
          setForm((prev) => ({ ...prev, sponsorName: data.referrerName }));
          setError((prev) => ({ ...prev, referralCode: "" }));
          toast.success(`Valid referral code. Sponsor: ${data.referrerName}`);
        }
      } catch (err) {
        setForm((prev) => ({ ...prev, sponsorName: "" }));
        setError((prev) => ({ ...prev, referralCode: "Error validating referral code" }));
        toast.error("Error validating referral code");
      }
    } else {
      setForm((prev) => ({ ...prev, sponsorName: "" }));
      setError((prev) => ({ ...prev, referralCode: "" }));
    }
  };

  const handleSendOtp = async () => {
    const email = form.email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setError((prev) => ({ ...prev, email: "Email is required to send OTP" }));
      toast.error("Enter your email to receive OTP");
      return;
    }
    if (!emailRegex.test(email)) {
      setError((prev) => ({ ...prev, email: "Invalid email format" }));
      toast.error("Please enter a valid email");
      return;
    }
    setOtpSending(true);
    setError((prev) => ({ ...prev, email: "", otp: "" }));
    try {
      const res = await fetch(`${API_BASE}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to send OTP");
      }
      setOtpSent(true);
      setOtpVerified(false);
      setOtpTimer(data?.resendInSeconds ?? 60);
      setOtp("");
      toast.success(data.message || "OTP sent to your email");
    } catch (err) {
      const message = err.message || "Failed to send OTP";
      setError((prev) => ({ ...prev, email: message }));
      toast.error(message);
    } finally {
      setOtpSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpSent) {
      toast.error("Please send OTP first");
      return;
    }
    if (!otp || otp.length !== 6) {
      setError((prev) => ({ ...prev, otp: "Enter the 6-digit OTP sent to your email" }));
      return;
    }
    setOtpVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email, otp })
      });
      const data = await res.json();
      if (!res.ok || data?.verified === false || data?.success === false) {
        throw new Error(data.message || "Invalid or expired OTP");
      }
      setOtpVerified(true);
      setError((prev) => ({ ...prev, otp: "", email: "" }));
      toast.success(data.message || "Email verified successfully");
    } catch (err) {
      const message = err.message || "Invalid or expired OTP";
      setOtpVerified(false);
      setError((prev) => ({ ...prev, otp: message }));
      toast.error(message);
    } finally {
      setOtpVerifying(false);
    }
  };
  // ==========================================

  const validateForm = () => {
    let errors = {};
    if (!form.email) {
      errors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email)) {
        errors.email = "Invalid email format";
      }
    }
    if (!otpVerified) {
      errors.email = "Please verify your email with OTP";
    }
    if (!form.password || form.password.length < 8) errors.password = "Password must be at least 8 characters";
    if (form.password !== form.confirmPassword) errors.confirmPassword = "Passwords do not match";
    if (!form.fullName || !form.fullName.trim()) {
      errors.fullName = "Full name is required";
    } else if (/\d/.test(form.fullName) || form.fullName.trim().length < 3) {
      errors.fullName = "Enter a valid full name";
    }
    if (!form.pinCode || form.pinCode.length !== 6) errors.pinCode = "Enter a valid 6-digit pin code";
    if (!form.state) errors.state = "State is required";
    if (!form.city) errors.city = "City is required";
    if (form.aadharNumber && !/^\d{12}$/.test(form.aadharNumber)) errors.aadharNumber = "Aadhar must be 12 digits";
    if (form.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.panNumber)) errors.panNumber = "Invalid PAN format";
    if (!form.mobile) errors.mobile = "Mobile number is required";
    else if (!/^\d{10}$/.test(form.mobile)) errors.mobile = "Mobile must be 10 digits";
    if (!form.address || !form.address.trim()) errors.address = "Address is required";
    if (!form.terms) errors.terms = "You must accept terms";
    if (!form.ageCheck) errors.ageCheck = "You must be at least 18";
    if (!form.gender) errors.gender = "Gender is required";
    if (form.referralCode && !form.sponsorName) {
      errors.referralCode = "Referral not allowed. The referrer has not purchased any package.";
    }
    if (!form.dob) {
      errors.dob = "Date of Birth is required";
    } else {
      const dob = new Date(form.dob);
      const today = new Date();
      if (isNaN(dob.getTime())) {
        errors.dob = "Invalid date";
      } else if (dob > today) {
        errors.dob = "Date cannot be in the future";
      } else {
        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
        if (age < 18) {
          errors.dob = "You must be at least 18 years old";
        }
      }
    }
    setError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!otpVerified) {
      setError((prev) => ({ ...prev, email: "Please verify your email with OTP", otp: "Email verification required" }));
      toast.error("Verify email OTP to continue");
      return;
    }
    setLoading(true);
    if (!form.referralCode) {
      form.referralCode = "ABBAS2026";
      form.sponsorName = "Mohammad Abbas Noorani";
    }
    if (!validateForm()) {
      toast.error("Please fix errors");
      setLoading(false);
      return;
    }
    try {
      const formData = new FormData();
      Object.keys(form).forEach((key) => formData.append(key, form[key]));
      const response = await signup(formData);
      const greetingText = response.greetingEmailSent
        ? "Welcome! Account created successfully. Greeting email sent."
        : "Welcome! Account created successfully.";

      if (response.warning) {
        toast.warning(`${response.warning.message}: ${response.warning.reason}`, { autoClose: 5000 });
        toast.success(greetingText);
      } else if (response.referralApplied) {
        toast.success(`Signup successful with referral applied! ${greetingText}`);
      } else {
        toast.success(greetingText);
      }
      setTimeout(() => navigate("/auth/sign-in"), 2000);
    } catch (err) {
      const errorMessage = err.message || "Signup failed!";
      toast.error(errorMessage);
      if (errorMessage.toLowerCase().includes("email already registered")) {
        setError((prev) => ({ ...prev, email: "This email is already registered" }));
      } else if (errorMessage.toLowerCase().includes("mobile number already registered")) {
        setError((prev) => ({ ...prev, mobile: "This mobile number is already registered" }));
      } else if (errorMessage.toLowerCase().includes("full name")) {
        setError((prev) => ({ ...prev, fullName: errorMessage }));
      } else if (errorMessage.toLowerCase().includes("email")) {
        setError((prev) => ({ ...prev, email: errorMessage }));
      } else if (errorMessage.toLowerCase().includes("mobile")) {
        setError((prev) => ({ ...prev, mobile: errorMessage }));
      } else if (errorMessage.toLowerCase().includes("date of birth") || errorMessage.toLowerCase().includes("18 years")) {
        setError((prev) => ({ ...prev, dob: errorMessage }));
      } else if (errorMessage.toLowerCase().includes("aadhar")) {
        setError((prev) => ({ ...prev, aadharNumber: errorMessage }));
      } else if (errorMessage.toLowerCase().includes("pan")) {
        setError((prev) => ({ ...prev, panNumber: errorMessage }));
      } else if (errorMessage.toLowerCase().includes("referral")) {
        setError((prev) => ({ ...prev, referralCode: errorMessage }));
      }
      setLoading(false);
    }
  };

  const renderError = (field) =>
    error[field] && <small className="text-red-500 text-xs mt-1 block">{error[field]}</small>;

  return (
    <div className="relative min-h-screen bg-slate-100 text-slate-900 transition-colors duration-300 dark:bg-slate-950 dark:text-slate-100">
      {/* Watermark Background */}
      <div className="pointer-events-none fixed inset-0 z-0 flex items-center justify-center opacity-30 dark:opacity-20">
        <img src={watermarkLogo} alt="Watermark" className="w-[80vw] max-w-[800px]" />
      </div>

      <ToastContainer position="top-right" autoClose={2000} />
      
      <Link
        to="/"
        className="absolute top-4 left-4 z-50 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-blue-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-blue-400 md:top-6 md:left-6"
      >
        <FaArrowLeft className="h-3 w-3" /> Back to Home
      </Link>

      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 lg:flex-row lg:items-start lg:gap-12 lg:px-6 pt-20 lg:pt-28 pb-12">
        {/* Left Side: Image & Content */}
        <section className="hidden w-full flex-col gap-6 lg:flex lg:w-5/12">
          {/* Image Box */}
          <div className="relative w-full overflow-hidden rounded-3xl shadow-xl aspect-square">
            <img
              src={HERO_IMAGE}
              alt="Secure network illustration"
              className="h-full w-full object-cover object-center"
              loading="lazy"
            />
            {/* Glassmorphic Overlay Badge */}
            <div className="absolute top-6 left-6 z-10 rounded-2xl border border-white/20 bg-slate-950/40 px-5 py-3 backdrop-blur-md shadow-2xl">
              <span className="text-sm font-bold uppercase tracking-[0.25em] text-white">
                Refer & Earn
              </span>
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 to-transparent"></div>
          </div>
          
          {/* Content Box */}
          <div className="rounded-3xl border border-slate-200/80 bg-white/80 p-8 shadow-xl backdrop-blur-xl dark:border-slate-700/50 dark:bg-slate-900/80">
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-blue-600 dark:text-blue-400">
              Join Our Network
            </p>
            <h1 className="mt-2 text-3xl font-semibold leading-tight text-slate-900 dark:text-white lg:text-4xl">
              Start Earning Today
            </h1>
            <p className="mt-3 text-base text-slate-600 dark:text-slate-300 font-medium">
              Create an account to gain access to premium features, build your network, and manage your payouts effortlessly.
            </p>
            <div className="mt-8 flex gap-4 text-sm">
              <div className="flex-1 rounded-2xl bg-blue-50/50 p-4 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50">
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">100%</p>
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-600/80 dark:text-blue-400/80">Secure</p>
              </div>
              <div className="flex-1 rounded-2xl bg-blue-50/50 p-4 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800/50">
                <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">Fast</p>
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-600/80 dark:text-blue-400/80">Payouts</p>
              </div>
            </div>
          </div>
        </section>

        {/* Right Side: Form */}
        <section className="flex flex-1 flex-col justify-center px-4 sm:px-6 lg:w-7/12 lg:px-0">
          <header className="mb-8 text-left">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600 dark:text-blue-400">
              Registration
            </p>
            <h2 className="mt-3 text-3xl font-bold text-slate-900 dark:text-slate-100">Create Your Account</h2>
            <p className="mt-2 text-base text-slate-600 dark:text-slate-400">
              Join our platform and start your journey with us today
            </p>
          </header>

          <div className="rounded-3xl border border-slate-200/80 bg-white/80 p-6 shadow-2xl backdrop-blur-xl dark:border-slate-700/50 dark:bg-slate-900/80 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
                <section>
                  <div className="mb-4 border-b border-slate-200 pb-3 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-blue-100 p-2.5 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
                        <PiUsersThree className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Personal Information</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Enter your basic details</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <InputField
                        id="fullName"
                        label="Full Name (as per Aadhar Card)*"
                        value={form.fullName}
                        onChange={handleChange}
                        placeholder="e.g., Arjun Pratap Singh"
                        className="rounded-2xl"
                      />
                      {renderError("fullName")}
                    </div>
                    <div>
                      <label htmlFor="gender" className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Gender*
                      </label>
                      <div className="mt-2 relative">
                        <select
                          id="gender"
                          className="block w-full h-12 appearance-none rounded-2xl border border-slate-200/80 bg-white px-4 text-sm outline-none transition focus:ring-2 focus:ring-blue-600 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-100"
                          value={form.gender}
                          onChange={handleChange}
                        >
                          {genderOptions.map((option) => (
                            <option key={option.value} value={option.value} className="bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100">
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-slate-400 dark:text-slate-500">⌄</span>
                      </div>
                      {renderError("gender")}
                    </div>
                    <div>
                      <InputField
                        id="dob"
                        type="date"
                        label="Date of Birth*"
                        value={form.dob}
                        onChange={handleChange}
                        max={new Date().toISOString().split("T")[0]}
                        className="rounded-2xl"
                      />
                      {renderError("dob")}
                    </div>
                  </div>
                </section>

                <section>
                  <div className="mb-4 border-b border-slate-200 pb-3 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-blue-100 p-2.5 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
                        <HiOutlineIdentification className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Contact Details</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Provide your contact information</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <InputField
                        id="mobile"
                        label="Mobile Number*"
                        value={form.mobile}
                        onChange={handleChange}
                        placeholder="10-digit mobile number"
                        className="rounded-2xl"
                      />
                      {renderError("mobile")}
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                        Email Address*
                      </label>
                      <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                        <input
                          id="email"
                          type="email"
                          className={`flex-1 h-12 rounded-2xl border px-4 py-3 text-sm outline-none transition focus:ring-2 focus:ring-blue-600 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-100 ${
                            otpVerified ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-500/10 dark:border-emerald-400" : "border-slate-200/80"
                          } ${otpVerified ? "cursor-not-allowed" : ""}`}
                          value={form.email}
                          onChange={handleChange}
                          disabled={otpVerified}
                          placeholder="name@company.com"
                        />
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={otpSending || otpTimer > 0 || otpVerified}
                          className={`h-12 rounded-2xl px-6 py-3 text-sm font-semibold text-white transition ${
                            otpSending || otpTimer > 0 || otpVerified
                              ? "bg-blue-400"
                              : "bg-blue-600 hover:bg-blue-700"
                          }`}
                          aria-live="polite"
                        >
                          {otpVerified ? "Verified" : otpSending ? "Sending..." : otpTimer > 0 ? `Resend ${otpTimer}s` : "Send OTP"}
                        </button>
                      </div>
                      {otpVerified && <small className="mt-1 block text-xs font-medium text-emerald-600">Email verified successfully.</small>}
                      {renderError("email")}
                    </div>
                    {otpSent && (
                      <div className="md:col-span-2">
                        <label htmlFor="otp" className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                          Verify OTP*
                        </label>
                        <div className="mt-2 flex flex-col gap-3 sm:flex-row">
                          <input
                            id="otp"
                            type="text"
                            inputMode="numeric"
                            maxLength="6"
                            className={`flex-1 h-12 rounded-2xl border px-4 py-3 text-sm tracking-[0.4em] outline-none focus:ring-2 focus:ring-emerald-500 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-100 ${
                              otpVerified ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-500/10 dark:border-emerald-400" : "border-slate-200/80"
                            }`}
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                            disabled={otpVerified}
                            placeholder="000000"
                          />
                          <button
                            type="button"
                            onClick={handleVerifyOtp}
                            disabled={otp.length !== 6 || otpVerifying || otpVerified}
                            className={`h-12 rounded-2xl px-6 py-3 text-sm font-semibold text-white transition ${
                              otpVerified
                                ? "bg-emerald-500/80"
                                : otp.length !== 6 || otpVerifying
                                  ? "bg-blue-400"
                                  : "bg-blue-600 hover:bg-blue-700"
                            }`}
                          >
                            {otpVerified ? "Verified" : otpVerifying ? "Verifying..." : "Verify Email to Continue"}
                          </button>
                        </div>
                        {renderError("otp")}
                      </div>
                    )}
                    <div>
                      <InputField id="pinCode" label="Pin Code*" value={form.pinCode} onChange={handlePincodeChange} placeholder="6-digit PIN" className="rounded-2xl" />
                      {renderError("pinCode")}
                    </div>
                    <div>
                      <InputField id="state" label="State*" value={form.state} onChange={handleChange} className="rounded-2xl" />
                      {renderError("state")}
                    </div>
                    <div>
                      <InputField id="city" label="City*" value={form.city} onChange={handleChange} className="rounded-2xl" />
                      {renderError("city")}
                    </div>
                    <div>
                      <InputField id="country" label="Country*" value={form.country} disabled className="rounded-2xl" />
                    </div>
                    <div className="md:col-span-2">
                      <TextField id="address" label="Full Address*" value={form.address} onChange={handleChange} rows={3} className="rounded-2xl" />
                      {renderError("address")}
                    </div>
                  </div>
                </section>

                <section>
                  <div className="mb-4 border-b border-slate-200 pb-3 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-blue-100 p-2.5 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
                        <PiShieldCheck className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Account Security</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Create a strong password</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-3">
                      <div className="relative">
                        <InputField
                          id="password"
                          label="Password*"
                          type={showPassword ? "text" : "password"}
                          value={form.password}
                          onChange={handleChange}
                          placeholder="At least 8 characters"
                          className="rounded-2xl"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="absolute bottom-0 right-4 top-[30px] flex items-center text-slate-500 transition hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                        </button>
                      </div>
                      {renderError("password")}
                      <div className="space-y-1 text-xs text-slate-500">
                        <div className="flex justify-between">
                          <span>Password strength</span>
                          <span className={`font-semibold ${passwordMeta.score >= 4 ? "text-emerald-600" : "text-amber-600"}`}>
                            {passwordMeta.label}
                          </span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-slate-100 dark:bg-slate-800">
                          <div
                            className={`h-full rounded-full ${passwordMeta.score >= 4 ? "bg-emerald-500" : "bg-amber-500"}`}
                            style={{ width: `${passwordMeta.percent}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                    <div className="relative">
                      <InputField
                        id="confirmPassword"
                        label="Confirm Password*"
                        type={showConfirmPassword ? "text" : "password"}
                        value={form.confirmPassword}
                        onChange={handleChange}
                        className="rounded-2xl"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-4 flex items-center text-slate-500 transition hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400"
                        aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                      >
                        {showConfirmPassword ? <FiEyeOff className="h-5 w-5" /> : <FiEye className="h-5 w-5" />}
                      </button>
                      {renderError("confirmPassword")}
                    </div>
                  </div>
                </section>

                <section>
                  <div className="mb-4 border-b border-slate-200 pb-3 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-blue-100 p-2.5 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400">
                        <HiOutlineDocumentText className="h-5 w-5" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Referral Information</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Add your referral code if available</p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <InputField
                        id="referralCode"
                        label="Referral Code (Optional)"
                        value={form.referralCode}
                        onChange={handleReferralChange}
                        placeholder="Enter sponsor code"
                        className="rounded-2xl"
                      />
                      {renderError("referralCode")}
                    </div>
                    <div>
                      <InputField id="sponsorName" label="Sponsor Name" value={form.sponsorName} disabled className="rounded-2xl" />
                    </div>
                  </div>
                </section>

                <section>
                  <div className="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-4 dark:border-slate-800 dark:bg-slate-800/60">
                    <div className="space-y-2">
                      <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <input
                          className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
                          type="checkbox"
                          id="terms"
                          checked={form.terms}
                          onChange={handleChange}
                        />
                        <span>
                          I agree to the <a href="https://www.rishtaforyou.com/terms-of-use" target="_blank" rel="noopener noreferrer" className="font-semibold text-blue-700 dark:text-blue-300 hover:underline">Terms and Conditions</a>
                        </span>
                      </label>
                      {renderError("terms")}
                      <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <input
                          className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-600"
                          type="checkbox"
                          id="ageCheck"
                          checked={form.ageCheck}
                          onChange={handleChange}
                        />
                        <span>I confirm that I am at least 18 years old.</span>
                      </label>
                      {renderError("ageCheck")}
                    </div>
                  </div>
                </section>

                <div className="space-y-4">
                  <button
                    type="submit"
                    disabled={loading || !otpVerified}
                    className={`w-full h-12 rounded-2xl px-4 py-3 text-sm font-semibold text-white shadow-xl shadow-blue-900/30 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950 ${
                      loading || !otpVerified
                        ? "cursor-not-allowed bg-blue-400"
                        : "bg-blue-600 hover:bg-blue-700 hover:shadow-2xl hover:shadow-blue-900/60"
                    }`}
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-3">
                        <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Registering account…
                      </span>
                    ) : (
                      otpVerified ? "Create Account" : "Verify Email to Continue"
                    )}
                  </button>
                  <p className="text-center text-sm text-slate-600 dark:text-slate-300">
                    Already have an account? {" "}
                    <Link to="/auth/sign-in" className="font-semibold text-blue-700 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300">
                      Sign in here
                    </Link>
                  </p>
                </div>
              </form>
            </div>
          </section>
      </div>
    </div>
  );
}
